import React from "react";
import { Star, Users, CalendarDays, BookUser, User } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearFlaggedQuestion } from "../../../../features/candidate-exam/candidate-exam-slice";
import toast from "react-hot-toast";
import { exams } from "../../../../api/exams";
import { useMutation } from "react-query";
import { differenceInDays } from "date-fns";
import Modal from "../../survey/components/Modal";
import DemographicsForm from "./Demographics";
import { useTranslation } from "react-i18next";
import Loader from "../../../../components/loader/Loader";
const SurveyCard = ({ survey, refetch }: { survey: any, refetch: any }) => {

  const navigate = useNavigate()
  const { t } = useTranslation();
  const dispatch = useDispatch()
  const location = useLocation();
  const currentLanguage = useSelector((state: any) => state.traslation.language);
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const user = JSON.parse(sessionStorage.getItem('user') || '{}')
  const isReview = location.pathname.includes("/candidate/my-review");
  const storedBranding = React.useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("branding") || '{}');
    } catch (error) {
      return null;
    }
  }, []);
  const appearance = storedBranding?.data?.appearance;
  // Branded culture audits should only show the client's palette — use
  // neutral greys for the blue/green status badges.
  const useNeutralStatus = user?.sub_type === 'culture' && !!appearance?.theme_colour;

  const startSurvey = (data: any) => {
    const questionResponse = data?.data?.questions
    sessionStorage.setItem('survey-questions', JSON.stringify(questionResponse))
    sessionStorage.setItem('survey-user', 'employee')
    navigate('/candidate/survey')
  }

  // Without this a failed question fetch did nothing visible. 4xx messages
  // are written for the participant (e.g. a culture audit random section
  // short of active questions names the section); 5xx ones aren't.
  const onStartSurveyError = (error: any) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;
    toast.error(status && status < 500 && message ? message : t('survey-start-failed'), { duration: 8000 });
  }

  const getQuestionsmutation = useMutation(exams.getExamQuestionsv2, {
    onSuccess: async (data: any) => {
      startSurvey(data)
    },
    onError: onStartSurveyError
  })

  const getReviewerQuestionsmutation = useMutation(exams.getReviewerExamQuestionsv2, {
    onSuccess: async (data: any) => {
      startSurvey(data)
    },
    onError: onStartSurveyError
  })

  // Fetching a survey's questions can take a few seconds on a slow
  // connection; until it returns the page looked unchanged, so people clicked
  // again. The overlay also blocks the other cards until we navigate away.
  const isLoadingQuestions = getQuestionsmutation.isLoading || getReviewerQuestionsmutation.isLoading;

  const takeSurvey = (exam: any) => {
    if (isLoadingQuestions) return;
    if (exam?.requireDemographics && !exam?.hasFilledDemographics) {
      //console.log(exam, 'exam');
      setOpenModal(true)
      return
    }
    if (/*parseInt(exam?.progress) >= 100 || */  /* exam?.completed_at*/ false) return toast.error(t('survey-already-completed'));
    //  else if(new Date(exam?.end_date).getTime() < new Date().getTime()) return toast.error('This survey has been closed');
    dispatch(clearFlaggedQuestion(''));
    window.sessionStorage.setItem("assessment-type", isReview ? "review" : "survey");
    window.sessionStorage.setItem("active-assessment-id", exam?.user_exam_id);
    window.sessionStorage.setItem("active-assessment-user-id", exam?.user_id);
    window.sessionStorage.setItem("active-assessment", JSON.stringify(exam));
    // Culture audit only: one UserExam can span several assessments, so the
    // specific assessment being taken must be carried through to Employee.tsx
    // for save/submit. 360/appraisal surveys don't set exam?.assessment?.id
    // here (undefined), so sessionStorage holds "" and every downstream call
    // keeps omitting assessmentId, same as before this change.
    window.sessionStorage.setItem("active-assessment-assessment-id", exam?.assessment?.id || "");
    if (isReview) {
      getReviewerQuestionsmutation.mutate({
        reviewerId: user?.user?.user_id || window.sessionStorage.getItem("user_id"),
        userId: exam?.user_id,
        userExamId: exam?.user_exam_id,
        language: currentLanguage,
      });
    }
    else {
      getQuestionsmutation.mutate({
        userId: user?.user?.user_id || window.sessionStorage.getItem("user_id"),
        examId: exam?.user_exam_id,
        userExamId: exam?.user_id,
        assessmentId: exam?.assessment?.id,
        showOnlyQuestions: true,
        language: currentLanguage,
      } as any);
    }
  }

  const viewResponse = (survey: any) => {
    sessionStorage.setItem('activeSurvey', JSON.stringify(survey))
    navigate(`/candidate/view-response`)
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 w-full  md:max-w-[320px]">
      {isLoadingQuestions && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/90"
        >
          <div className="h-24 overflow-hidden flex items-center">
            <Loader />
          </div>
          <p className="mt-2 text-gray-600 text-base">{t("loading-survey")}</p>
        </div>
      )}
      {openModal &&
        <Modal
          closeModal={(value: any) => setOpenModal(value)}
          title=""
        >
          <div className="flex -mt-8 items-center gap-2">
            <DemographicsForm setOpen={setOpenModal} refetch={refetch} survey={survey} />
          </div>
        </Modal>

      }
      <div className="flex items-start justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{survey?.group_title || survey?.survey_title}</h2>
        <div className="flex items-center gap-2">
          {
            parseInt(survey?.progress) === 0 ?
              <span className={`${useNeutralStatus ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-darkBlue'} w-[80px] text-center text-xs font-medium px-1 py-0.5 rounded-full`}>
                {t("not-started")}
              </span>
              :
              (parseInt(survey?.progress) >= 100 || survey?.completed_at) ?
                <span className={`${useNeutralStatus ? 'bg-gray-200 text-gray-900' : 'bg-green-100 text-primary'} w-[80px] text-center text-xs font-medium px-1 py-0.5 rounded-full`}>
                  {t("completed")}
                </span>
                :
                <span className="bg-orange-100 w-[80px] text-center text-orange-500 text-xs font-medium px-1 py-0.5 rounded-full">
                  {t("in-progress")}
                </span>
          }
        </div>
      </div>

      {/* Meta Info */}
      {/* <div className="flex items-center text-gray-500 text-md mt-3 gap-2">
        <BookUser className="w-4 h-4" />
        <span>{survey?.exam_title || survey?.survey_title}</span>
      </div> */}
      <div className="flex items-center text-gray-500 text-md mt-1 gap-2">
        <CalendarDays className="w-4 h-4" />
        <span>
          {(() => {
            const today = new Date();
            const endDate = new Date(survey?.end_date);
            const diff = differenceInDays(endDate, today);

            if (diff > 0) return t("days-left", { count: diff });
            if (diff === 0) return t("ends-today");
            return t("expired");
          })()}
        </span>
      </div>

      {location.pathname.includes("/candidate/my-review") &&
        <div className="flex items-center text-gray-500 text-md mt-3 gap-2">
          <User className="w-4 h-4" />
          <span>{survey?.user_name}</span>
        </div>
      }
      {location.pathname.includes("/candidate/my-review") &&
        <div className="flex items-center text-orange-500 text-md mt-3 gap-2" style={{ color: appearance?.theme_colour }}>
          <span>{survey?.review_type}</span>
        </div>
      }
      {/* !location.pathname.includes("/candidate/my-review") &&
        <div className="flex items-center text-gray-500 text-md mt-3 gap-2">
        Overall Rating:
        <span>{survey?.overall_rating || '-'}</span>
      </div>
       */}
      {/* !location.pathname.includes("/candidate/my-review") &&
        <>
          <div className="flex items-center font-semibold text-gray-500 text-md mt-3 gap-2">
            Rating By Section
          </div>
          { !survey?.sectionRatings?.length ? null :
            survey?.sectionRatings?.map((rating: any) => (
              <div className="flex items-center text-gray-500 text-md mt-2 gap-2">
              {rating?.sectionTitle} :
              <span>{rating?.averageRating || '-'}</span>
             </div>
            ))
          }
        </>
       */}
      {/* Progress */}
      <div className="mt-4">
        <div className="text-md text-gray-700 font-medium mb-1">{t("progress")}</div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div style={{ width: `${parseInt(survey?.progress)}%`, backgroundColor: appearance?.theme_colour }} className="h-full bg-primary"></div>
        </div>
      </div>

      <div className={`${isReview ? 'flex justify-between' : ''} `}>
        {/* Continue Button */}
        <button id="take-survey" onClick={() => takeSurvey(survey)} disabled={isLoadingQuestions} style={{ backgroundColor: (!survey?.completed_at && appearance?.theme_colour) ? appearance.theme_colour : undefined }} className={`w-full mt-4 ${parseInt(survey?.progress) === 0 ? ' bg-orange-500' : /* parseInt(survey?.progress) >= 100 || */ survey?.completed_at ? 'bg-gray-300' : 'bg-orange-500'
          } text-white text-sm font-medium py-2 rounded-md flex justify-center items-center gap-2`}>
          <span>{
         /* parseInt(survey?.progress) === 0 || */ !survey?.completed_at ? isReview ? t('evaluate') : t('take-survey') : /*parseInt(survey?.progress) >= 100 ||*/ survey?.completed_at ? t('completed') : t('continue')}</span>
        </button>
        {false ? <button onClick={() => viewResponse(survey)} className={`w-full mt-4 bg-primary ml-5 text-white text-sm font-medium py-2 rounded-md flex justify-center items-center gap-2`}>
          {t("view-response")}
        </button> : null}
      </div>
    </div>
  );
};

export default SurveyCard;
